import React from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  setPage,
  setTake,
  selectSearchCriteria,
  selectEstado,
} from "./actions";
import Searcher from "~styled/searcher/searcher";
import { Typography } from "@mui/material";
import Selector, { type OptionKV } from "~styled/selector/Selector";
import Pagination from "~styled/pagination/Pagination";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { paginationOptions } from "~constants/pagination";
import { estadoEndosoFilter, estadoEndosoOptions } from "~constants/endosos";

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 20,
    width: "100%",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  searchers: {
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingBottom: 20,
  },
  filters: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  label: {
    fontSize: "10px",
    fontWeight: 500,
    lineHeight: "1.3",
    textAlign: "left",
    color: theme.colours.grey2,
    paddingLeft: 5,
    paddingBottom: 5,
  },
  horizontalSpace: { paddingRight: "10px" },
  horizontalSpaceSearcher: { paddingRight: "10px", width: "100%" },
  searcherAndTitle: {
    display: "flex",
    flexDirection: "column",
    width: "220px",
  },
  dateSelectorAndTitle: {
    display: "flex",
    flexDirection: "column",
    width: "220px",
  },
}));

type EstadoValue = string | number;
type RowsValue = string | number;

interface SliceEndosos {
  isLoading: boolean;
  totalRegistros: number;
  take: RowsValue;
  page: number;
  estado: EstadoValue;
  searchCriteria: string;
}

const useReduxSelector = () =>
  useSelector<any, SliceEndosos>((state) => ({
    isLoading: state.listadoEndososReducer.isLoading,
    totalRegistros: state.listadoEndososReducer.totalRegistros,
    take: state.listadoEndososReducer.take,
    page: state.listadoEndososReducer.page,
    estado: state.listadoEndososReducer.estado,
    searchCriteria: state.listadoEndososReducer.searchCriteria,
  }));

const messageLoading = "Cargando su consulta previa" as const;

const FiltrosEndosos: React.FC = () => {
  const { isLoading, totalRegistros, take, page, searchCriteria, estado } =
    useReduxSelector();

  const dispatch = useDispatch();

  const handleChange = <T,>(
    event: { target: { value: T } },
    action: (v: T) => any
  ) => {
    dispatch(setPage(0));
    dispatch(action(event.target.value));
  };

  const handleChangePage = (value: number) => {
    dispatch(setPage(value));
  };

  const classes = useStyles();

  const estadoLabelMap = estadoEndosoOptions as Record<string, string>;

  const paginationOptionsStr = React.useMemo(
    () =>
      paginationOptions.map((o) => ({
        label: String(o.label),
        value: o.value,
      })),
    []
  );

  return (
    <>
      <div className={classes.root}>
        <>
          <div className={classes.filtersContainer}>
            <div className={classes.filters}>
              <div className={classes.searcherAndTitle}>
                <Typography variant="h3" className={classes.label}>
                  ESTADO
                </Typography>

                <div className={classes.horizontalSpace}>
                  <Selector<EstadoValue>
                    value={estadoLabelMap[String(estado)]}
                    options={estadoEndosoFilter as OptionKV<EstadoValue>[]}
                    onChange={(value) =>
                      handleChange({ target: { value } }, selectEstado)
                    }
                    onClickDisabled={() => {
                      dispatch(queueMessage(messageLoading));
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className={classes.searcherAndTitle}>
                <Typography variant="h3" className={classes.label}>
                  BUSCAR
                </Typography>
                <div className={classes.horizontalSpaceSearcher}>
                  <Searcher
                    borderSquared
                    value={searchCriteria}
                    filter={searchCriteria}
                    placeholder="N° póliza, Dominio, Doc Asegurado"
                    onChange={(event) =>
                      handleChange(event, selectSearchCriteria)
                    }
                    onClear={() =>
                      handleChange(
                        { target: { value: "" } },
                        selectSearchCriteria
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {!isLoading && (
              <div className={classes.filters}>
                <Pagination<RowsValue>
                  rowsPerPage={take}
                  onChangeRowsPerPage={(value) =>
                    handleChange({ target: { value } }, setTake)
                  }
                  page={page}
                  onChangePage={handleChangePage}
                  totalRegistros={totalRegistros}
                  options={paginationOptionsStr as OptionKV<RowsValue>[]}
                />
              </div>
            )}
          </div>
        </>
      </div>
    </>
  );
};

export default FiltrosEndosos;
